import { Locale, OrigamiUrls } from '@app/origami-api/constants';
import { DomManipulate } from '@app/shared/utils/dom-manipulate';
import * as Sentry from '@sentry/angular';
import { BuildProps } from '@app/origami-api/build-props';
import { LocaleSwitcher } from '@app/session/locales-switcher.service';
import { fixLocale } from '@app/origami-api/constants/default-locales';
import { getServerConfig } from '@app/origami-api/server-config';
import { EnvironmentType } from '@app/origami-api/constants/environment-type';

function tiSettingsInit(): Promise<void> {
  return new Promise((resolve) => {
    DomManipulate.addScriptToDocumentHead(OrigamiUrls.settingsScript, resolve);
  });
}

function sentryInit() {
  const cfg = getServerConfig().serverCfg;

  if (cfg.sentryUILogDSN) {
    Sentry.init({
      dsn: cfg.sentryUILogDSN,
      tracesSampleRate: isNaN(+cfg.tracesSampleRate) ? 0 : +cfg.tracesSampleRate || 0,
      ignoreErrors: ['ExpressionChangedAfterItHasBeenCheckedError'],
      environment: cfg.sentryEnvironment || location.hostname,
      integrations: [Sentry.browserTracingIntegration()],
    });
  }
}

function loadScript(script: string): Promise<void> {
  return new Promise((resolve) => DomManipulate.addScriptToDocumentHead(script, resolve));
}

function getBuildProps(): Promise<void> {
  return new Promise((resolve) => {
    const req = new XMLHttpRequest();
    req.addEventListener('load', () => {
      try {
        BuildProps.init(JSON.parse(req.responseText));
      } catch (e) {
        BuildProps.init({});
      }
      // this is for Classic UI
      window.localStorage.setItem('ti-origin', BuildProps.origin);

      resolve();
    });
    req.open('GET', `assets/build-props.json?nocache=${Date.now()}`);
    req.send();
  });
}

function urlEncodeIfNecessary(s) {
  let regex = /[\\\"<>\.;]/;
  let hasBadChars = regex.exec(s) != null;
  return hasBadChars && typeof encodeURIComponent != 'undefined' ? encodeURIComponent(s) : s;
}

export function getQueryParamValue(param) {
  let q = document.location.search || document.location.hash;
  //rework with list of server params later if needed
  if (q.indexOf('?soff=') === 0) {
    q = document.location.hash;
  }
  if (q) {
    if (/\?/.test(q)) {
      q = q.split('?')[1];
    }
    if (param == null) {
      return urlEncodeIfNecessary(q);
    }
    let pairs = q.split('&');
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].substring(0, pairs[i].indexOf('=')) == param) {
        return urlEncodeIfNecessary(pairs[i].substring(pairs[i].indexOf('=') + 1));
      }
    }
  }
  return '';
}

export function updateFavIcon(et: EnvironmentType) {
  /*
  let color;

  switch(et) {
    case EnvironmentType.Dev:
      color = '#ff4a4a';
      return;
    case EnvironmentType.Testing:
      color = '#9013fe';
      return;
    case EnvironmentType.DemoEnv:
      color = '#ff9500';
      return;
    case EnvironmentType.Sandbox:
      color = '#fae800';
      return;
    case EnvironmentType.Staging:
      color = '#6adcd5';
      return;
    case EnvironmentType.Production:
      color = '#5391c6';
      return;
  }
  */
  if (et) {
    const iconName = `assets/favicons/favicon-${et}.svg`;

    let link = document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'shortcut icon';
    link.href = iconName;

    let head = document.getElementsByTagName('head')[0];

    // removing default favicon
    let links = head.getElementsByTagName('link');
    for (let i = 0; i <= links.length; i++) {
      if (links[i].rel == 'shortcut icon') {
        head.removeChild(links[i]);
        break;
      }
    }

    document.getElementsByTagName('head')[0].appendChild(link);
  }
}

// TODO: not sure if this is a good solution; check later for a better performance
function fixFlagCssPaths(): void {
  const oldPath = '/assets/flags/countries-flags';
  const newPath = `${document.baseURI}assets/flags/countries-flags`;

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) {
        if (rule instanceof CSSStyleRule) {
          const bg = rule.style.backgroundImage;
          if (bg.includes(oldPath)) {
            rule.style.backgroundImage = bg.replaceAll(oldPath, newPath);
          }
        }
      }
    } catch {
      // cross-origin stylesheets throw SecurityError — skip
    }
  }
}

export function init(): Promise<any> {
  fixFlagCssPaths();
  return getBuildProps()
    .then(() => tiSettingsInit())
    .then(() => {
      sentryInit();

      const scripts = [`assets/scripts/init.js?nocache=${BuildProps.OrigamiBuildNumber}`].map((script) =>
        loadScript(script),
      );

      const lang = fixLocale(getQueryParamValue('lang')) as Locale;

      return Promise.all([
        ...scripts,
        LocaleSwitcher.init(lang).then(() => {
          updateFavIcon(+getServerConfig().environmentType as EnvironmentType);
        }),
      ]);
    });
}
